import {
  createDailyChallengeDefinition,
  type DailyChallengeIdentity,
  type DailyChallengeDefinition,
} from "../config/daily-challenge-config";
import type { ModeId } from "../config/gameplay-config";
import type { GameFlow } from "./game-flow";
import type { ProgressState } from "./progress-state";

export interface DailyChallengeProgressPort {
  getState(): Pick<ProgressState, "dailyChallengeRecords">;
}

export interface DailyChallengeSeedPort {
  useChallenge(seed: string): void;
  useNormal(): void;
}

export interface DailyChallengeSnapshot {
  readonly bestLevel: number;
  readonly challenge: DailyChallengeDefinition;
  readonly completed: boolean;
}

const cloneChallenge = (
  challenge: DailyChallengeDefinition,
): DailyChallengeDefinition => ({ ...challenge });

export class DailyChallengeRuntime {
  private activeChallenge: DailyChallengeDefinition | null = null;

  public constructor(
    private readonly flow: GameFlow,
    private readonly progress: DailyChallengeProgressPort,
    private readonly seeds: DailyChallengeSeedPort,
    private readonly now: () => number,
  ) {
    flow.subscribe((snapshot) => {
      if (snapshot.scene === "home" && this.activeChallenge !== null) {
        this.activeChallenge = null;
        this.seeds.useNormal();
      }
    });
  }

  public getActiveChallenge(): DailyChallengeDefinition | null {
    return this.activeChallenge === null
      ? null
      : cloneChallenge(this.activeChallenge);
  }

  public getTodaySnapshot(): DailyChallengeSnapshot {
    const challenge = createDailyChallengeDefinition(this.now());
    const record = this.progress
      .getState()
      .dailyChallengeRecords.find(
        (candidate) => candidate.identity === challenge.identity,
      );
    return {
      bestLevel: record?.bestLevel ?? 0,
      challenge,
      completed: record?.completed ?? false,
    };
  }

  public replay(challenge: DailyChallengeDefinition): boolean {
    if (this.flow.getSnapshot().scene !== "results") {
      return false;
    }
    return this.startChallenge(challenge);
  }

  public startMode(mode: ModeId): boolean {
    if (this.flow.getSnapshot().scene !== "home") {
      return false;
    }
    this.activeChallenge = null;
    this.seeds.useNormal();
    this.flow.startMode(mode);
    return true;
  }

  public startIdentity(identity: DailyChallengeIdentity): boolean {
    if (this.flow.getSnapshot().scene !== "home") {
      return false;
    }
    const challenge = createDailyChallengeDefinition(this.now());
    if (challenge.identity !== identity) {
      return false;
    }
    return this.startChallenge(challenge);
  }

  public startToday(): boolean {
    if (this.flow.getSnapshot().scene !== "home") {
      return false;
    }
    return this.startChallenge(createDailyChallengeDefinition(this.now()));
  }

  private startChallenge(challenge: DailyChallengeDefinition): boolean {
    this.activeChallenge = cloneChallenge(challenge);
    this.seeds.useChallenge(challenge.seed);
    this.flow.startMode(challenge.mode);
    return true;
  }
}
